import { action, computed, makeObservable, observable } from 'mobx';
import { defaultGoogleMapsSetting } from '../constants/googleMapsConfig';
import { IResponseGoogleMapsSettings, TGoogleMapsSettings, googleMapsSettingsCast } from '../types/googleMapsConfig';
import client from '~/services/client';
import logger from '~/services/logger';
import { LoadingStatus } from '~/types/main';

class GoogleMapsConfigStore {
  constructor() {
    makeObservable(this);
  }

  /** For check what field's are changed */
  private _initialSettings: TGoogleMapsSettings = defaultGoogleMapsSetting;
  @observable public settings: TGoogleMapsSettings = defaultGoogleMapsSetting;
  @observable public loadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public isDataEdited: boolean = false;

  @computed public get isLoading(): boolean {
    return [LoadingStatus.Loading, LoadingStatus.Refreshing].includes(this.loadingStatus);
  }

  @action public fetchGoogleMapsSettings = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const response: IResponseGoogleMapsSettings = await client.get('v3/integrations/google/maps');

      const settings = googleMapsSettingsCast(response);
      this._initialSettings = settings;
      this.settings = settings;

      this.loadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'googleMapsConfigStore fetchGoogleMapsSettings', showModal: true });
    }
  };

  @action public changeSettings = (data: Partial<TGoogleMapsSettings>) => {
    this.settings = { ...this.settings, ...data };
    this.isDataEdited = true;
  };

  @action public updateGoogleMapsSettings = async () => {
    try {
      this.loadingStatus = LoadingStatus.Loading;

      const { publicGoogleApiKey: publicKey, privateGoogleApiKey: privateKey } = this.settings;
      const { publicGoogleApiKey: initialPublicKey, privateGoogleApiKey: initialPrivateKey } = this._initialSettings;

      const params = {
        google_api_key: publicKey === initialPublicKey ? undefined : publicKey || null,
        google_api_key_private: privateKey === initialPrivateKey ? undefined : privateKey || null,
      };

      await client.post('v3/integrations/google/maps', params);

      this.isDataEdited = false;
      this.loadingStatus = LoadingStatus.Success;

      return true;
    } catch (error: any) {
      this.loadingStatus = LoadingStatus.Error;
      logger.error({ error, place: 'googleMapsConfigStore updateGoogleMapsSettings', showModal: true });
    }
  };

  @action public reset = () => {
    this.settings = defaultGoogleMapsSetting;
    this._initialSettings = defaultGoogleMapsSetting;
    this.loadingStatus = LoadingStatus.Init;
    this.isDataEdited = false;
  };
}

const googleMapsConfigStore = new GoogleMapsConfigStore();

export default googleMapsConfigStore;
